import { select } from '@angular-redux/store';
import { Component, forwardRef, Inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BillingSubscriptionWinComponent } from "sharedFeatures/billing";
import { ChangePaymentWinComponent } from 'sharedFeatures/change-payment-win';
import { LicenseFrequency, LicensePackageOptions, LicensePackageType } from 'sharedFeatures/enums';
import { Account, Subscription, CommonApp } from "sharedFeatures/sharedModels";
import { BillingService, DialogCreationService } from "sharedFeatures/sharedServices";
import { accountActions } from 'sharedFeatures/store';
import { SubscriptionCancelWinComponent } from 'sharedFeatures/subscription-cancel-win';
import { UpgradeWin } from "sharedFeatures/upgrade-win";
import { AuthUtils, Utils } from 'sharedFeatures/utils';
import { BillingSubscriptionPaneComponent } from './billing-subscription-pane.component';
import { ISSOLinkResponse } from "sharedFeatures/sharedServices/Responses/Subscription/ISSOLinkResponse";
import { IErrorResponse, isSuccessResponse } from "sharedFeatures/sharedServices/Responses";
import { DomSmallUtils } from 'sharedGuiSmall/utils/dom-utils';
import { LoadingSpinnerService } from 'sharedGuiSmall/sharedServices/loadingSpinner.service';
import { BuyPlanWinComponent } from '../../buy-plans-win';
import * as moment from 'moment';

@Component(
    {
        selector: 'billing-subscription-body',
        templateUrl: './billing-subscription-body.component.html',
        styleUrls: ['./billing-subscription-body.component.less']
    })

export class BillingSubscriptionBodyComponent {

    billingFrequency = $localize`per year`;
    errorText = $localize`Error`;

    UserSubscription: Subscription = new Subscription();
    subcriptionType: string = '';
    licenseExpired: boolean = false;
    isMonthly: boolean = false;
    switchPackageBtnVisible: boolean = false;
    fee: string  = "-";
    expirationDate: string = "-";
    paymentMethod: string = "-";
    useRedText: boolean = false;
    downgradePlansCount: string = '';
    downgradeDate: string = '';
    price: number = 0;
    startupPrice: number = 99;
    businessPrice: number = 199;
    proPrice: number = 299;

    additionalLicensePrice: number = 149.5;
    
    // Unsubscribe event
    private storeUnsubscribe = new Subject<void>();

    @select(['account', 'accountData'])
    readonly accountData$!: Observable<Account>;
    private account: Account = new Account();

    @select(['account', 'isSubscriptionDowngradeActive'])
    readonly isSubscriptionDowngradeActive$!: Observable<boolean>;

    @select(['account', 'downgradeDate'])
    readonly downgradeDate$!: Observable<string>;

    @select(['account', 'subscriptionPriceOption1'])
    readonly subscriptionPriceOption1$!: Observable<string>;

    @select(['account', 'subscriptionPriceOption2'])
    readonly subscriptionPriceOption2$!: Observable<string>;

    @select(['accountAppData', 'accountAppComponent'])
    readonly accountAppComponent$!: Observable<any>;
    private accountAppComponent: any;

    @select(['appData', 'appComponent'])
    readonly appComponent$!: Observable<any>;
    private appComponent: any | undefined;

    constructor(
        private billingService: BillingService,
        @Inject(forwardRef(() => BillingSubscriptionPaneComponent)) private parent: BillingSubscriptionPaneComponent,
        private dialogCreation: DialogCreationService,
        private loadingSpinnerService: LoadingSpinnerService
    ) {

        //this.licenseExpired = this.parent.app.AuthData.licenseStatus == "Expired";
        const frequency = AuthUtils.getLicenseFrequency(this.UserSubscription.PriceOptionCodes);
        this.subcriptionType = AuthUtils.getLicensePackageType(this.UserSubscription.PriceOptionCodes);
        // Hide upgrade button when license is Professional Annually
        this.switchPackageBtnVisible = (frequency !== LicenseFrequency.Annually);

        this.isMonthly = frequency == LicenseFrequency.Monthly;
        this.UserSubscription = parent.UserSubscription;
        this.UpdateSubscription();
    }

    ngOnInit(): void {
        // Subscribe on the redux store
        this.subscribeReduxStore();
    }

    ngOnDestroy() {
        // Unsubscribe from the redux store
        this.storeUnsubscribe.next();
        this.storeUnsubscribe.complete();
    }

    // Subscribe on the redux store
    private subscribeReduxStore(): void {
        this.isSubscriptionDowngradeActive$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => {
                if (this.UserSubscription)
                    this.UserSubscription.IsSubscriptionDowngradeActive = x;
            });

        this.downgradeDate$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => {
                if (this.UserSubscription)
                    this.UserSubscription.DowngradeDate = x;
            });

        this.subscriptionPriceOption1$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => {
                if (this.UserSubscription)
                    this.UserSubscription.DowngradePriceOption1 = x;
            });

        this.subscriptionPriceOption2$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => {
                if (this.UserSubscription)
                    this.UserSubscription.DowngradePriceOption2 = x;
            });

        this.appComponent$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => {
                this.appComponent = x;
            });

        this.accountAppComponent$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe((x: any) => {
                this.accountAppComponent = x;
                
            });
    }
    
    // Get license product version
    public getLicenseProductVersion(): string {
        if (!this.UserSubscription || !this.UserSubscription.ProductVersion)
            return "";

        return this.UserSubscription.ProductVersion;
    }

    // get package type
    public getPackageType(): string {
        const packageType = AuthUtils.getLicensePackageType(this.UserSubscription.PriceOptionCodes);
        let licenseName = LicensePackageOptions.get(packageType)!.OptionName;

        if (packageType == LicensePackageType.BrixxSingle) {
            licenseName += ` ${AuthUtils.getBrixxPlansCount(this.UserSubscription.PriceOptionCodes[0])}`;
        }

        return licenseName ?? '';
    }

    BuyNow(): void {
        this.UserSubscription.AutoRenew = true;

    }

    Cancel(): void {
        this.UserSubscription.AutoRenew = false;

    }

    RefreshSubscriptions(): void {
        //this.getHostWindow().setZIndex(PopupComponent.getTopestZ());

        this.loadingSpinnerService.setLoading(true);
        this.billingService.LoadSubscriptions(true);
        this.parent.refreshSubscriptions(() => {
            this.UpdateSubscription();
        });
    }

    UpdateSubscription(): void {

        accountActions.setSubscriptionDowngradeActive(this.UserSubscription.IsSubscriptionDowngradeActive);
        accountActions.setSubscriptionDowngradeDate(this.UserSubscription.DowngradeDate);
        accountActions.setSubscriptionPriceOption1(this.UserSubscription.DowngradePriceOption1);
        accountActions.setSubscriptionPriceOption2(this.UserSubscription.DowngradePriceOption2);

        this.useRedText = !this.UserSubscription.AutoRenew;
        this.fee = this.UserSubscription.Fee;
        this.expirationDate = this.UserSubscription.ExpirationDateString;
        this.downgradeDate = moment(this.UserSubscription.DowngradeDate).format('MMM DD, YYYY');

        if (this.UserSubscription.IsSubscriptionDowngradeActive) {
            this.useRedText = true;
            this.downgradePlansCount = `${this.UserSubscription.DowngradePriceOption1.replace("Brixx", "")}`
        }

        this.paymentMethod = this.UserSubscription.PaymentMethod;
        this.loadingSpinnerService.setLoading(false);

        /* In future, we might show an additional tab for expired subscriptions.
        if (!this.licenseExpired) {
            this.useRedText = !this.UserSubscription.AutoRenew;
            this.fee = this.UserSubscription.Fee;
            this.expirationDate = this.UserSubscription.ExpirationDateString;

            if (this.UserSubscription.IsSubscriptionDowngradeActive) {
                this.useRedText = true;
            }

            this.paymentMethod = this.UserSubscription.PaymentMethod;
            Utils.setLoading(false);
        }
        else {
            Utils.setLoading(false);

            const endDate: moment.Moment = moment(this.parent.app.AuthData.licenseEndDate);

            this.expirationDate = "Ended on " + endDate.format('DD MMMM YYYY');
            this.useRedText = true;
        }*/
    }

    LoadSSOLink(pageType: string): void {
        if (this.UserSubscription != null)
        {
            let wnd = window.open('', '_blank');
            this.billingService.LoadSSOLink(this.UserSubscription.SubscriptionReference, pageType)
                .then((data: ISSOLinkResponse | IErrorResponse) => {
                    if (isSuccessResponse(data)) {
                        let ssoLink = (<ISSOLinkResponse>data).ssoLink;
                        if (wnd)
                            wnd.location.href = ssoLink;
                    }
                })
                .catch(error => this.dialogCreation.createUnknownExceptionDialog(error).show());
        }
    }

    onBottomLinkClick(): void {
        this.parent.onBottomLinkClick();
    }

    getApp(): CommonApp {
        if (this.appComponent)
            return this.appComponent;
        else if (this.accountAppComponent)
            return this.accountAppComponent;

        throw new Error('Architecture error.');
    }

    onSubscriptionSwitchClick(): void {
        localStorage.setItem('isFromManageBillingPanel', 'true');
        let upgradeWin = <UpgradeWin>this.getApp().createDialog(UpgradeWin);
        upgradeWin.setSize(DomSmallUtils.width(document.body), DomSmallUtils.height(document.body));

        upgradeWin.getAfterViewInit().subscribe(() => {
            upgradeWin.getBodyComponent().isFromManageBillingPanel = true;
            upgradeWin.getBodyComponent().isNewUser = false;

            upgradeWin.getBodyComponent().initSubscriptionParams(
                this.UserSubscription.SubscriptionReference,
                AuthUtils.getLicenseFrequency(this.UserSubscription.PriceOptionCodes),
                AuthUtils.getLicensePackageType(this.UserSubscription.PriceOptionCodes),
                this.getLicenseProductVersion(),
                this.UserSubscription.LicenseCompanies.length > 0 ? this.UserSubscription.LicenseCompanies.join(';') : '0',
                this.UserSubscription.Fee
            );
        });

        upgradeWin.show(0, 0);
    }

    getHostWindow(): BillingSubscriptionWinComponent {
        // get BillingSubscriptionWin reference
        return this.parent.parent.parent;
    }

    onSubscriptionCancelClick(): void {
        let appElem = this.getApp().getElement().getBoundingClientRect();

        if (this.UserSubscription.AutoRenew) {
            let cancelDialog = <SubscriptionCancelWinComponent>this.getApp().createDialog(SubscriptionCancelWinComponent);
            cancelDialog.billingComponent = this;
            cancelDialog.hostWindow = this.getHostWindow();
            let x = (appElem.width - cancelDialog.width) / 2;
            let y = (appElem.height - cancelDialog.height) / 2;

            cancelDialog.getAfterViewInit().subscribe(() => {
                cancelDialog.getBodyComponent().setSubscriptionRef(this.UserSubscription.SubscriptionReference);
            });

            //cancelDialog.hostWindow.setZIndex(PopupComponent.getTopestZ());
            cancelDialog.show(x, y, undefined, true);
        }
        else
        {
            // renew subscription
            this.loadingSpinnerService.setLoading(true);
            this.billingService.ChangeSubscriptionAutoRenew(this.UserSubscription.SubscriptionReference, true)
                .then((data) => {
                    if (isSuccessResponse(data)) {
                        this.RefreshSubscriptions();
                    }
                    else {
                        this.loadingSpinnerService.setLoading(false);
                        const popup = this.dialogCreation.createErrorWarning(this.errorText, data.errorMessage, () => { popup.close(); });
                        popup.show();
                    }
                })
                .catch(error => {
                    this.loadingSpinnerService.setLoading(false);
                    this.dialogCreation.createUnknownExceptionDialog(error).show();
                });
        }
    }

    onUpdateCreditCardClick(): void {
        let appElem = this.getApp().getElement().getBoundingClientRect();

        let changeDialog = <ChangePaymentWinComponent>this.getApp().createDialog(ChangePaymentWinComponent);
        changeDialog.billingComponent = this;
        changeDialog.hostWindow = this.getHostWindow();
        let x = (appElem.width - changeDialog.width) / 2;
        let y = (appElem.top + 80);

        //changeDialog.hostWindow.setZIndex(PopupComponent.getTopestZ());
        changeDialog.show(x, y);
        // todo BR-5133 this.LoadSSOLink("change_card");
    }

    onSubscriptionSubscribeAgainClick(): void {
        this.getApp().menuOnUpgrade();
    }

    onSubscriptionDowngradeCancelClick(): void {

        if (this.UserSubscription) {

            this.billingService.CancelSubscriptionDowngrade(this.UserSubscription.SubscriptionReference)
                .then((data) => {
                    if (isSuccessResponse(data)) {
                        this.UserSubscription.IsSubscriptionDowngradeActive = false;
                        this.useRedText = false;
                        accountActions.setSubscriptionDowngradeActive(this.UserSubscription.IsSubscriptionDowngradeActive);
                    }
                    else {
                        const popup = this.dialogCreation.createErrorWarning(this.errorText, data.errorMessage, () => { popup.close(); });
                        popup.show();
                    }
                })
                .catch(error => {
                    this.dialogCreation.createUnknownExceptionDialog(error).show();
                });
        }
    }

    toChangeSubscriptionPlan(): void {
        let app = this.dialogCreation.getDialogCreator();
        let buyPlanWin = <BuyPlanWinComponent>app.createDialog(BuyPlanWinComponent);

        buyPlanWin.getAfterViewInit().subscribe(() => {

        });

        buyPlanWin.setSize(DomSmallUtils.width(document.body), DomSmallUtils.height(document.body));
        buyPlanWin.show(0, 0);

        buyPlanWin.onclose.subscribe((res) => {
            this.UpdateSubscription();
        });

    }

}
